import type { CatalogItemType } from './lib';

/**
 * Every property `CreateCatalogItemDto` declares, and nothing else.
 *
 * `Tarabeeza-Backend/src/main.ts` sets `forbidNonWhitelisted: true` globally, so
 * a property the DTO does not declare is not stripped and not ignored — it
 * rejects the whole request with 400 and takes every other field on the form
 * down with it. The product form is where that bites hardest: the design draws
 * a cost price, a tax group, a prep time, an "allow takeaway" switch, a
 * "visible on menu" switch and a draft/publish pair, none of which the DTO has
 * ever declared.
 *
 * So the body is assembled from this allowlist rather than spread from form
 * state. A field added to the form later cannot reach the wire by accident; it
 * has to be named here, which is the moment someone checks the DTO.
 */
export const CATALOG_ITEM_DTO_FIELDS = [
  'name',
  'type',
  'categoryId',
  'stationId',
  'sku',
  'barcode',
  'description',
  'imageUrl',
  'basePrice',
  'taxRate',
  'sortOrder',
  'availability',
  'metadata',
  'isSoldOut',
  'isActive',
] as const;

export interface CatalogItemBodyInput {
  name: string;
  type: CatalogItemType;
  categoryId: string;
  stationId: string;
  sku: string;
  barcode: string;
  description?: string;
  /** As typed, in major units; converted to the number the DTO validates. */
  basePrice: string;
  /** A fraction — `@Min(0) @Max(1)`. 0.15 is 15%. */
  taxRate: string;
  sortOrder: string;
  isActive: boolean;
}

/** `undefined` for an empty optional: the DTO's `@IsString()` refuses `''`. */
function text(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * The body of `POST /catalog` / `PATCH /catalog/:id`.
 *
 * `basePrice` arrives already clamped to the currency's minor unit by the
 * caller's `toMoney` — this only turns it into the number `@IsNumber` wants,
 * because a decimal string fails that validator.
 */
export function catalogItemBody(input: CatalogItemBodyInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: input.name.trim(),
    type: input.type,
    basePrice: Number(input.basePrice) || 0,
    // A tax rate typed as "15" would be refused by `@Max(1)`; clamping states
    // the fraction rule the field's own hint already gives, rather than
    // bouncing the whole save on it.
    taxRate: clamp(Number(input.taxRate), 0, 1),
    sortOrder: Math.max(0, Math.trunc(Number(input.sortOrder) || 0)),
    isActive: input.isActive,
  };

  const optional = {
    categoryId: text(input.categoryId),
    stationId: text(input.stationId),
    sku: text(input.sku),
    barcode: text(input.barcode),
    description: text(input.description),
  };
  for (const [key, value] of Object.entries(optional)) {
    if (value !== undefined) body[key] = value;
  }

  return body;
}
